import { format } from 'date-fns';
import { z } from 'zod';
import { ConstraintSchema, constraintFields } from '../../../../lib/account-constraints';
import type { AccountConstraint } from '@/types/schemas';

export const searchSchema = z.object({
  ngay_ct1: z.string().optional(),
  ngay_ct2: z.string().optional(),
  so_ct1: z.string().optional().nullable(),
  so_ct2: z.string().optional().nullable(),
  ma_ngv: z.string().optional(),
  ma_gd: z.string().optional(),
  ma_kh: z.string().optional(),
  tk: z.string().optional(),
  ma_nvbh: z.string().optional(),
  dien_giai: z.string().optional().nullable(),
  ma_vt: z.string().optional(),
  ma_kho: z.string().optional(),
  ma_lo: z.string().optional(),
  ma_vi_tri: z.string().optional(),
  ma_unit: z.string().optional(),
  status: z.string().optional(),
  user_id0: z.string().optional()
});

export type SearchFormValues = z.infer<typeof searchSchema>;
export const initialSearchValues: SearchFormValues = {
  status: ''
};

const baseSchema = z.object({
  hdbh_yn: z.boolean().nullable().optional(),
  px_yn: z.boolean().nullable().optional(),
  pt_tao_yn: z.boolean().nullable().optional(),
  ma_httt: z.string().nullable().optional(),
  ck_yn: z.boolean().nullable().optional(),
  loai_ck: z.coerce.string().nullable().optional(),
  ck_tl_nt: z.coerce.number().nullable().optional(),
  transfer_yn: z.boolean().nullable().optional(),
  ten_kh_thue: z.string().nullable().optional(),
  dia_chi: z.string().nullable().optional(),
  ma_so_thue: z.string().nullable().optional(),
  ong_ba: z.string().nullable().optional(),
  e_mail: z.string().nullable().optional(),
  dien_giai: z.string().nullable().optional(),
  du_cn_thu: z.coerce.number().nullable().optional(),
  ngay_ct: z.string().nullable().optional(),
  ngay_ct0: z.string().nullable().optional(),
  so_ct_hddt0: z.string().nullable().optional(),
  ngay_ct_hddt0: z.string().nullable().optional(),
  so_ct2_hddt0: z.string().nullable().optional(),
  ngay_px: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  ma_nt: z.string().nullable().optional(),
  ty_gia: z.coerce.number().nullable().optional(),
  ma_tthddt: z.string().nullable().optional(),
  ma_pttt: z.string().nullable().optional(),
  so_ct2_hddt: z.string().nullable().optional(),
  so_ct0: z.string().nullable().optional(),
  so_ct: z.string().nullable().optional(),
  so_ct2: z.string().nullable().optional(),
  ma_mau_ct_hddt: z.string().nullable().optional(),
  ma_gd: z.string().nullable().optional(),
  tknh: z.string().nullable().optional(),
  dien_giai_nh: z.string().nullable().optional(),
  ten_vt_thue: z.string().nullable().optional(),
  ly_do_huy: z.string().nullable().optional(),
  ly_do: z.string().nullable().optional(),
  so_bk: z.string().nullable().optional(),
  ngay_bk: z.string().nullable().optional(),
  ghi_chu: z.string().nullable().optional(),
  t_so_luong: z.number().nullable().optional(),
  fixed_tax_yn: z.boolean().nullable().optional(),
  t_tien_nt2: z.number().nullable().optional(),
  t_thue_nt: z.number().nullable().optional(),
  t_tien_nt: z.number().nullable().optional(),
  t_ck_nt: z.number().nullable().optional(),
  t_tt_nt: z.number().nullable().optional(),
  tien_tck_nt: z.number().nullable().optional(),
  tl_ck: z.number().nullable().optional(),
  ck_nt: z.number().nullable().optional(),
  ma_kh: z.preprocess(value => value ?? '', z.string().min(1, 'Mã khách hàng không được để trống')),
  tk: z.preprocess(value => value ?? '', z.string().min(1, 'Tài khoản nợ không được để trống')),
  chi_tiet: z
    .array(
      z.object({
        ma_vt: z.preprocess(value => value ?? '', z.string().min(1, 'Mã vật tư không được để trống')),
        ma_kho: z.preprocess(value => value ?? '', z.string().min(1, 'Mã kho không được để trống')),
        so_luong: z.preprocess(
          value => {
            if (value === '' || value === null || value === undefined) return 0;
            const numericValue = Number(value);
            return Number.isNaN(numericValue) ? 0 : numericValue;
          },
          z
            .number({
              required_error: 'Số lượng phải lớn hơn 0',
              invalid_type_error: 'Số lượng phải lớn hơn 0'
            })
            .gt(0, { message: 'Số lượng phải lớn hơn 0' })
        ),
        ...constraintFields
      })
    )
    .optional()
});

export const formSchema = (constraints: AccountConstraint[] = []) => ConstraintSchema(baseSchema, constraints);
export type FormSchema = z.infer<ReturnType<typeof formSchema>>;

export const initialFormValues: FormSchema = {
  hdbh_yn: true,
  px_yn: true,
  pt_tao_yn: false,
  ma_httt: 'TMB',
  ck_yn: false,
  loai_ck: '1',
  ck_tl_nt: 0,
  transfer_yn: false,
  ten_kh_thue: '',
  dia_chi: '',
  ma_so_thue: '',
  ong_ba: '',
  e_mail: '',
  dien_giai: '',
  du_cn_thu: 0,
  so_ct: '',
  ma_kh: '',
  tk: '',
  chi_tiet: [],
  ngay_ct: format(new Date(), 'yyyy-MM-dd'),
  ngay_ct0: format(new Date(), 'yyyy-MM-dd'),
  so_ct_hddt0: '',
  ngay_ct_hddt0: format(new Date(), 'yyyy-MM-dd'),
  so_ct2_hddt0: '',
  ngay_px: format(new Date(), 'yyyy-MM-dd'),
  status: '5',
  ma_nt: '',
  ty_gia: 1,
  ma_tthddt: '1',
  ma_pttt: '',
  so_ct2_hddt: null,
  ma_mau_ct_hddt: null,
  ma_gd: '',
  tknh: '',
  dien_giai_nh: '',
  ten_vt_thue: '',
  ly_do_huy: '',
  ly_do: '',
  so_bk: '',
  ngay_bk: '',
  ghi_chu: '',
  t_so_luong: 0,
  fixed_tax_yn: false,
  t_tien_nt2: 0,
  t_thue_nt: 0,
  t_tien_nt: 0,
  t_ck_nt: 0,
  t_tt_nt: 0,
  tien_tck_nt: 0,
  tl_ck: 0,
  ck_nt: 0
};
